import { useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { DataTable, type Column } from '@/components/DataTable';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatNumber } from '@/i18n/format';
import type { InventoryRow } from '@/api/types';
import { StatCard } from '@/components/StatCard';

const LIMIT = 50;

/**
 * COMMERCIAL LAYER epic (ADR-075 Part C). Seller's own stock: SKU, barcode
 * (SKU code doubles as the scannable identifier in this schema - there is
 * no separate barcode column upstream), available/reserved/on-hand, and
 * location kind (surfaces QUARANTINE without exposing warehouse-internal
 * slotting the seller has no reason to see). No serial/KIZ unit listing
 * here: `portalInventory` (apps/api/src/portalReads.ts) does not join
 * serial_unit at all - regulated per-unit detail was out of this pass's
 * scope on the read side, same as it was for the previous agent's backend
 * pass (not invented here).
 */
export function InventoryPage(): JSX.Element {
  const [sku, setSku] = useState('');
  const [offset, setOffset] = useState(0);
  const result = useApi(() => endpoints.inventory({ sku: sku || undefined, limit: LIMIT, offset }), [sku, offset]);
  const { t, locale } = useLanguage();

  const columns: readonly Column<InventoryRow & { id: string }>[] = [
    { key: 'sku', header: 'SKU', render: (r) => r.sku_code },
    { key: 'name', header: t('inventory.colName'), render: (r) => r.sku_name },
    { key: 'location', header: t('inventory.colLocation'), render: (r) => `${r.location_code} (${r.location_kind.toLowerCase()})` },
    { key: 'bucket', header: t('inventory.colBucket'), render: (r) => t(`inventory.bucket.${r.stock_bucket}`) },
    { key: 'on_hand', header: t('inventory.colOnHand'), align: 'right', render: (r) => formatNumber(r.qty_on_hand, locale) },
    { key: 'reserved', header: t('inventory.colReserved'), align: 'right', render: (r) => formatNumber(r.qty_reserved, locale) },
    { key: 'available', header: t('inventory.colAvailable'), align: 'right', render: (r) => formatNumber(r.qty_available, locale) },
    { key: 'serial', header: t('inventory.colSerialTracked'), render: (r) => (r.is_serial_tracked ? t('common.yes') : t('common.no')) },
  ];

  const rows =
    result.status === 'success' ? { rows: result.data.rows.map((r) => ({ ...r, id: `${r.sku_id}:${r.location_id}` })), total: result.data.total } : undefined;

  return (
    <div>
      <div className="page-header">
        <h1>{t('nav.inventory')}</h1>
      </div>
      <div className="filters">
        <label>
          {t('inventory.skuOrName')}
          <input
            value={sku}
            onChange={(e) => {
              setSku(e.target.value);
              setOffset(0);
            }}
            placeholder={t('common.search')}
          />
        </label>
      </div>
      {result.status === 'success' && result.data.summary && (
        <div className="grid-cards" role="group" aria-label={t('inventory.summary.title')}>
          <StatCard label={t('inventory.summary.inBins')} value={formatNumber(result.data.summary.qty_in_bins, locale)} />
          <StatCard label={t('inventory.summary.available')} value={formatNumber(result.data.summary.qty_available, locale)} />
          <StatCard label={t('inventory.summary.quarantine')} value={formatNumber(result.data.summary.qty_quarantine, locale)} />
          <StatCard label={t('inventory.summary.inProcess')} value={formatNumber(result.data.summary.qty_in_process, locale)} />
          <StatCard label={t('inventory.summary.shipped')} value={formatNumber(result.data.summary.qty_shipped, locale)} />
        </div>
      )}
      <DataTable
        result={rows ? { status: 'success', data: rows, reload: result.reload } : (result as never)}
        columns={columns}
        limit={LIMIT}
        offset={offset}
        onPageChange={setOffset}
        emptyTitle={t('inventory.emptyTitle')}
        emptyHint={sku ? t('inventory.emptyHintSearch') : t('inventory.emptyHintNoStock')}
      />
    </div>
  );
}
